import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { SystemSettings } from './systemSettings';
import { StatusPage } from './status';
import { SecurityManager } from './securityManager';
import { SecurityManager2 } from './securityManager2';
import { TokenManager } from './tokenManager/tokenManager';
import { UserManagement } from './userAccounts/userAccounts';
import {
  ProvisionUserAccountsPage,
  UserAccountRequestProviderSettingsPage,
  UserAccountRequestOrganizationsSettingsPage,
  UserAccountRequestRolesSettingsPage
} from './userAccountRequests';
import { PermissionAliases } from './userSecurity/PermissionAliases';
import { MassDeactivate } from './massDeactivate';
import { AdminEmailManager } from './adminEmail/AdminEmailManager';
import { InboundMailRoutingPage } from './adminEmail/InboundMailRoutingPage';
import { MassEmailPage } from './massCommunication/massCommunication';
import { ListAllMessages } from './messages/listAllMessages';
import { ResolveMissingEmailsPage } from './messages/ResolveMissingEmailsPage';
import NotificationSettings from './notifications/NotificationSettings';
import { BulkEnrollmentEmailsPage } from './bulkEnrollmentEmails';
import { TwoFactorEnrollmentPage } from './twoFactorEnrollment/TwoFactorEnrollmentPage';
import { DatabaseDictionaryPage } from './dataDictonary/dataDictonary';
import { ErrorLogs } from './errorLogs/ErrorLogs';
import { ChangeAuditPage } from './changeAudit/ChangeAuditPage';
import { HelpTopicsManager } from './help/helpTopics';
import { Help as HelpAdmin } from './help/help';
import { EfaxAdminPage } from './efax/EfaxAdminPage';
import { GqlDevGuidePage } from '../dev/gqlDevGuide';
import { GqlTypesPage } from '../dev/gqlTypes';
import { AdminDevCenter } from './dev/AdminDevCenter';
import { ChampArrestMatcher } from '../tools/champArrestMatcher';
import { ProjectSheetExtractor } from '../tools/kpiExtractor/projectSheetExtractor';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getUserPolicyList, policyMatches } from '@/lib/policies';
import { useUser } from '@/store/useUser';
import {
  Settings,
  Shield,
  Mail,
  Database,
  Users,
  MessageSquare,
  AlertTriangle,
  HelpCircle,
  Key,
  FileText,
  Send,
  UserX,
  Activity,
  Wrench,
  Search,
  Star,
  StarOff,
  ShieldCheck,
  GitMerge,
  FileSearch,
  Timer
} from 'lucide-react';
import SponsorManagementPage from './sponsorManagement';
import NewsSettings from './news/newsSettings';
import ReferralUnacceptableChargesPage from './referralUnacceptableCharges/ReferralUnacceptableChargesPage';
import CronManagementPage from './cronManagement/CronManagementPage';
import { DatabaseDiagnosticsPage } from './databaseDiagnostics/DatabaseDiagnosticsPage';

const ParticipantMergePage = require('./ParticipantMergePage').ParticipantMergePage as React.ComponentType<any>;
const ResolverPolicyReportPage = require('./securityPolicyReport/ResolverPolicyReportPage').ResolverPolicyReportPage as React.ComponentType<any>;

interface AdminPanel {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  path: string;
  keywords: string[];
  category: string;
  policies?: string[];
  hidden?: boolean;
}

export const adminPanels: AdminPanel[] = [
  {
    id: 'inbound-mail-routing',
    title: 'Inbound Mail Routing',
    description: 'Manage support staff recipients and subject routing rules',
    icon: Mail,
    path: '/admin/inbound-mail-routing',
    keywords: ['inbound', 'email', 'mail', 'support', 'subject', 'routing', 'rules', 'recipients'],
    category: 'Communication',
    policies: ['admin:access~full']
  },
  {
    id: 'email-manager',
    title: 'Email Manager',
    description: 'Manage email system and queues',
    icon: Mail,
    path: '/admin/email-manager',
    keywords: ['email', 'mail', 'smtp', 'queue', 'messages'],
    category: 'Communication',
    policies: ['admin:access~full']
  },
  {
    id: 'mass-communication',
    title: 'Email All Users',
    description: 'Send bulk messages and notifications',
    icon: Send,
    path: '/admin/mass-communication',
    keywords: ['mass', 'communication', 'bulk', 'broadcast', 'notification'],
    category: 'Communication',
    policies: ['admin:access~full']
  },
  {
    id: 'news',
    title: 'News',
    description: 'Manage login and dashboard announcements',
    icon: MessageSquare,
    path: '/admin/news',
    keywords: ['news', 'announcements', 'login', 'dashboard', 'content'],
    category: 'Communication',
    policies: ['admin:access~full']
  },
  {
    id: 'notification-templates',
    title: 'Notification Templates',
    description: 'Manage notification recipients and templates',
    icon: Send,
    path: '/admin/notifications',
    keywords: ['notifications', 'email', 'templates', 'recipients', 'alerts'],
    category: 'Communication',
    policies: ['admin:access~full']
  },
  {
    id: 'efax',
    title: 'eFax',
    description: 'Access eFax recipient management',
    icon: Send,
    path: '/admin/efax',
    keywords: ['efax', 'fax', 'recipients', 'legacy'],
    category: 'Communication',
    policies: ['tools:efax:access~full']
  },
  {
    id: 'data-dictionary',
    title: 'Data Dictionary',
    description: 'View and manage data definitions',
    icon: Database,
    path: '/admin/data-dictionary',
    keywords: ['data', 'dictionary', 'schema', 'definitions', 'metadata'],
    category: 'Developer',
    policies: ['admin:access~full']
  },
  {
    id: 'change-audit',
    title: 'Change Audit',
    description: 'Review candidate change history',
    icon: Activity,
    path: '/admin/change-audit',
    keywords: ['audit', 'changes', 'history', 'edit', 'tracking'],
    category: 'Reports',
    policies: ['admin:access~full']
  },
  {
    id: 'referral-unacceptable-charges',
    title: 'Unacceptable Charges',
    description: 'Maintain the referral exclusion charge list used by referral intake automation',
    icon: Database,
    path: '/admin/referral-unacceptable-charges',
    keywords: ['referral', 'charges', 'unacceptable', 'bad charge', 'exclusion', 'public defender'],
    category: 'Data',
    policies: ['tools:publicdefenderslist~full']
  },
  {
    id: 'dev-center',
    title: 'Developer Tools',
    description: 'Documentation and schema exploration tools',
    icon: FileText,
    path: '/admin/dev',
    keywords: ['dev', 'developer', 'schema', 'graphql', 'docs'],
    category: 'Developer',
    policies: ['admin:access~full']
  },
  {
    id: 'dev-guide',
    title: 'Dev Guide',
    description: 'Queries, mutations, and subscriptions reference',
    icon: FileText,
    path: '/admin/dev/guide',
    keywords: ['graphql', 'gql', 'schema', 'functions', 'reference'],
    category: 'Developer',
    policies: ['admin:access~full']
  },
  {
    id: 'dev-types',
    title: 'GraphQL Types',
    description: 'Type definitions and usage',
    icon: FileText,
    path: '/admin/dev/types',
    keywords: ['graphql', 'types', 'schema', 'fields', 'reference'],
    category: 'Developer',
    policies: ['admin:access~full']
  },
  {
    id: 'provider-settings',
    title: 'Account Provisioning Settings',
    description: 'Manage providers, organizations, sites, and request roles',
    icon: Database,
    path: '/admin/settings/providers',
    keywords: ['provider', 'program', 'assignment', 'dropdown', 'options', 'organization', 'site', 'roles'],
    category: 'System',
    policies: ['admin:access~full']
  },
  {
    id: 'error-logs',
    title: 'Error Logs',
    description: 'View system error logs and debugging info',
    icon: AlertTriangle,
    path: '/admin/error-logs',
    keywords: ['error', 'logs', 'debug', 'troubleshoot', 'issues'],
    category: 'Developer',
    policies: ['admin:access~full']
  },
  {
    id: 'security-manager',
    title: 'Security Manager',
    description: 'Manage security settings and access controls',
    icon: Shield,
    path: '/admin/security-manager',
    keywords: ['security', 'access', 'permissions', 'roles', 'auth'],
    category: 'Security',
    policies: ['admin:access~full']
  },
  {
    id: 'security-manager2',
    title: 'Security Manager v2',
    description: 'Preview: streamlined security management',
    icon: ShieldCheck,
    path: '/admin/security-manager2',
    keywords: ['security', 'access', 'permissions', 'roles', 'auth', 'preview'],
    category: 'Security',
    policies: ['admin:access~full']
  },
  {
    id: 'security-policy-report',
    title: 'Policy Report',
    description: 'Read-only resolver policy audit and endpoint mapping report',
    icon: FileSearch,
    path: '/admin/security-policy-report',
    keywords: ['security', 'policy', 'report', 'resolver', 'audit', 'permissions'],
    category: 'Security',
    policies: ['admin:access~full']
  },
  {
    id: 'permission-aliases',
    title: 'Permission Groups',
    description: 'Manage reusable permission bundles',
    icon: ShieldCheck,
    path: '/admin/permission-aliases',
    keywords: ['security', 'permissions', 'group', 'bundle', 'access'],
    category: 'Security',
    policies: ['admin:access~full']
  },
  {
    id: 'token-manager',
    title: 'Token Manager',
    description: 'Manage authentication tokens and API keys',
    icon: Key,
    path: '/admin/token-manager',
    keywords: ['token', 'api', 'key', 'authentication', 'jwt'],
    category: 'Security',
    policies: ['admin:access~full']
  },
  {
    id: 'help',
    title: 'Support Tickets',
    description: 'Manage user support tickets',
    icon: HelpCircle,
    path: '/admin/help',
    keywords: ['help', 'support', 'assistance', 'documentation'],
    category: 'Support',
    policies: ['tools:support:tickets~full']
  },
  {
    id: 'help-topics',
    title: 'Help Topics',
    description: 'Manage help documentation and topics',
    icon: HelpCircle,
    path: '/admin/help/topics',
    keywords: ['help', 'topics', 'documentation', 'guide', 'support'],
    category: 'Support',
    policies: ['tools:support:tickets~full']
  },
  {
    id: 'participant-merge',
    title: 'Participant Merge',
    description: 'Search duplicate participants and merge them in bulk',
    icon: GitMerge,
    path: '/admin/tools/participant-merge',
    keywords: ['merge', 'participants', 'duplicates', 'cleanup', 'data cleanup', 'dob', 'cin', 'cid', 'champid', 'ssn', 'fin'],
    category: 'Data',
    policies: ['admin:access~full']
  },
  {
    id: 'status',
    title: 'System Status',
    description: 'View system health and status information',
    icon: Activity,
    path: '/admin/status',
    keywords: ['status', 'health', 'monitoring', 'system', 'performance'],
    category: 'System',
    policies: ['admin:access~full']
  },
  {
    id: 'database-diagnostics',
    title: 'System Diagnostics',
    description: 'Check application, server, memory, and database health',
    icon: Database,
    path: '/admin/database-diagnostics',
    keywords: ['system', 'server', 'memory', 'database', 'diagnostics', 'latency', 'performance', 'connection', 'timing'],
    category: 'System',
    policies: ['admin:access~full']
  },
  {
    id: 'cron-management',
    title: 'Cron Management',
    description: 'View, schedule, disable, and run background jobs',
    icon: Timer,
    path: '/admin/crons',
    keywords: ['cron', 'jobs', 'schedule', 'background', 'run', 'automation'],
    category: 'System',
    policies: ['admin:access~full']
  },
  {
    id: 'system-settings',
    title: 'System Settings',
    description: 'Configure system-wide settings and preferences',
    icon: Settings,
    path: '/admin/system-settings',
    keywords: ['system', 'settings', 'configuration', 'preferences', 'global'],
    category: 'System',
    policies: ['admin:access~full']
  },
  {
    id: 'user-accounts',
    title: 'User Accounts',
    description: 'Manage user accounts and profiles',
    icon: Users,
    path: '/admin/user-accounts',
    keywords: ['user', 'accounts', 'profiles', 'members', 'people'],
    category: 'Users',
    policies: ['admin:access~full']
  },
  {
    id: 'provision-user-accounts',
    title: 'Provision User Accounts',
    description: 'Convert approved requests into user accounts',
    icon: ShieldCheck,
    path: '/admin/provision-user-accounts',
    keywords: ['user', 'provision', 'security_users', 'create', 'onboarding'],
    category: 'Users',
    policies: ['admin:access~full']
  },
  {
    id: 'bulk-enrollment-emails',
    title: 'Bulk Enrollment Emails',
    description: 'Send enrollment emails from a pasted list',
    icon: Mail,
    path: '/admin/bulk-enrollment-emails',
    keywords: ['bulk', 'enrollment', 'email', 'totp', 'two-factor'],
    category: 'Users',
    policies: ['admin:access~full'],
    hidden: true
  },
  {
    id: 'two-factor-enrollment',
    title: 'Two-Factor Enrollment',
    description: 'List users without 2FA and send enrollment invites',
    icon: ShieldCheck,
    path: '/admin/two-factor-enrollment',
    keywords: ['twofactor', '2fa', 'enrollment', 'totp', 'security'],
    category: 'Security',
    policies: ['admin:access~full']
  },
  // Removed Generate User Link and User Authorization panels
];

export const visibleAdminPanels = adminPanels.filter((panel) => !panel.hidden);

const categories = [
  { name: 'Communication', color: 'text-orange-600 bg-orange-50 border-orange-200' },
  { name: 'Data', color: 'text-cyan-600 bg-cyan-50 border-cyan-200' },
  { name: 'Security', color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { name: 'Support', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  { name: 'System', color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { name: 'Users', color: 'text-green-600 bg-green-50 border-green-200' }
];

const AdminCenter: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [localFavorites, setLocalFavorites] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateAdminFavorites } = useUser();

  // Sync local favorites with user settings
  useEffect(() => {
    if (user?.settings?.adminSettingsFavorites) {
      setLocalFavorites(user.settings.adminSettingsFavorites);
    }
  }, [user]);

  // Toggle favorite status
  const toggleFavorite = async (panelId: string) => {
    const newFavorites = localFavorites.includes(panelId)
      ? localFavorites.filter(id => id !== panelId)
      : [...localFavorites, panelId];
    
    // Update local state immediately for responsive UI
    setLocalFavorites(newFavorites);
    
    // Update backend - state is already updated in useUser hook
    const result = await updateAdminFavorites(newFavorites);
    if (!result.success) {
      console.error('Failed to update favorites:', result.error);
      // You could show a toast notification here
    }
  };

  const policyList = useMemo(() => {
    return getUserPolicyList(user);
  }, [user?.policies]);

  const wildcard = policyList.includes('*:*~*');

  const canAccessPanel = (panel: AdminPanel) => {
    if (!user) return false;
    if (wildcard) return true;

    const requiredPolicies = Array.isArray(panel.policies) && panel.policies.length > 0
      ? panel.policies
      : [];
    if (requiredPolicies.length === 0) return false;
    if (policyList.length === 0) return false;

    return requiredPolicies.some((requiredPolicy) =>
      policyList.some((userPolicy) => policyMatches(userPolicy, requiredPolicy))
    );
  };

  const componentMap: { [key: string]: React.ComponentType } = {
    '/admin/efax': EfaxAdminPage,
    '/admin/system-settings': SystemSettings,
    '/admin/settings/providers': UserAccountRequestProviderSettingsPage,
    '/admin/settings/user-account-request-roles': UserAccountRequestRolesSettingsPage,
    '/admin/settings/user-account-request-organizations': UserAccountRequestOrganizationsSettingsPage,
    '/admin/status': StatusPage,
    '/admin/database-diagnostics': DatabaseDiagnosticsPage,
    '/admin/crons': CronManagementPage,
    '/admin/security-manager': SecurityManager,
    '/admin/security-manager2': SecurityManager2,
    '/admin/security-policy-report': ResolverPolicyReportPage,
    '/admin/permission-aliases': PermissionAliases,
    '/admin/token-manager': TokenManager,
    '/admin/user-accounts': UserManagement,
    '/admin/provision-user-accounts': ProvisionUserAccountsPage,
    '/admin/email-manager': AdminEmailManager,
    '/admin/inbound-mail-routing': InboundMailRoutingPage,
    '/admin/mass-communication': MassEmailPage,
    '/admin/notifications': NotificationSettings,
    '/admin/news': NewsSettings,
    '/admin/messages/all': ListAllMessages,
    '/admin/data-dictionary': DatabaseDictionaryPage,
    '/admin/dev': AdminDevCenter,
    '/admin/dev/guide': GqlDevGuidePage,
    '/admin/dev/types': GqlTypesPage,
    '/admin/dev/gqlSchema': GqlDevGuidePage,
    '/admin/error-logs': ErrorLogs,
    '/admin/change-audit': ChangeAuditPage,
    '/admin/referral-unacceptable-charges': ReferralUnacceptableChargesPage,
    '/admin/help/topics': HelpTopicsManager,
    '/admin/help': HelpAdmin,
    '/admin/tools/participant-merge': ParticipantMergePage,
    '/admin/bulk-enrollment-emails': BulkEnrollmentEmailsPage,
    '/admin/two-factor-enrollment': TwoFactorEnrollmentPage,
  };

  const filteredPanels = useMemo(() => {
    let panels = visibleAdminPanels.filter((panel) => canAccessPanel(panel));

    if (selectedCategory === 'Favorites') {
      panels = panels.filter(panel => localFavorites.includes(panel.id));
    }
    
    if (selectedCategory && selectedCategory !== 'Favorites') {
      panels = panels.filter(panel => panel.category === selectedCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      panels = panels.filter(panel => 
        panel.title.toLowerCase().includes(query) ||
        panel.description.toLowerCase().includes(query) ||
        panel.keywords.some(keyword => keyword.toLowerCase().includes(query))
      );
    }

    return panels;
  }, [searchQuery, selectedCategory, localFavorites, policyList, user, wildcard]);

  const groupedPanels = useMemo(() => {
    const groups: { [key: string]: AdminPanel[] } = {};
    
    // If viewing favorites, group them as "Favorites"
    if (selectedCategory === 'Favorites') {
      groups['Favorites'] = filteredPanels;
    }
    
    // If no specific category selected, group favorites at top
    if (!selectedCategory && localFavorites.length > 0) {
      const favoritePanels = visibleAdminPanels
        .filter(panel => localFavorites.includes(panel.id))
        .filter(panel => canAccessPanel(panel));
      if (favoritePanels.length > 0) {
        groups['Favorites'] = favoritePanels;
      }
    }
    
    filteredPanels.forEach(panel => {
      // Skip favorites if they're already grouped
      if (selectedCategory === 'Favorites') {
        return;
      }
      
      // Skip adding to category groups if this is a favorite and we're showing all
      if (!selectedCategory && localFavorites.includes(panel.id)) {
        return;
      }
      
      if (!groups[panel.category]) {
        groups[panel.category] = [];
      }
      groups[panel.category].push(panel);
    });

    return groups;
  }, [filteredPanels, selectedCategory, localFavorites]);

  const handlePanelClick = (panel: AdminPanel) => {
    navigate(panel.path);
  };

  const isActivePanel = (panel: AdminPanel) => {
    return location.pathname === panel.path;
  };

  return (
    <div className="flex h-full gap-4 px-0 pb-4 sm:px-1 lg:px-1 xl:px-2">
	      <div className="app-page-shell flex w-80 flex-col overflow-hidden">
	        <div className="border-b border-[var(--neutral-gray-30)] bg-[var(--surface-panel-solid)]">
          <div className="px-3 py-3">
            <div className="app-kicker mb-1.5 text-[var(--neutral-gray-120)]">Administration</div>
            <h1 className="mb-3 text-xl font-semibold text-[var(--neutral-gray-120)]">Admin Center</h1>
            
            <div className="relative w-full">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 transform text-gray-400" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 w-full pl-8"
                autoComplete="off"
                name="admin-search"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-3 pt-4">
          {Object.entries(groupedPanels).map(([category, panels]) => (
            <div key={category} className="mb-4">
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                {category}
              </h3>
              <div className="space-y-1">
                {panels.map(panel => {
                  const Icon = panel.icon;
                  const isFavorite = localFavorites.includes(panel.id);
                  return (
                    <div key={panel.id} className="relative group/panel">
                      <button
                        id={`admin-panel-${panel.id}-open`}
                        onClick={() => handlePanelClick(panel)}
                        className={cn(
                          "group flex w-full items-center rounded-lg border px-2.5 py-2 text-left transition-all duration-150",
                          isActivePanel(panel)
                            ? "border-[var(--neutral-gray-30)] bg-[var(--neutral-gray-10)] text-[var(--azure-primary)]"
                            : "border-transparent text-gray-700 hover:bg-[var(--neutral-gray-10)] hover:text-gray-900"
                        )}
                      >
                        <Icon className={cn(
                          "mr-2.5 h-4 w-4 flex-shrink-0",
                          isActivePanel(panel) ? "text-[var(--azure-primary)]" : "text-gray-400 group-hover:text-gray-600"
                        )} />
                        <div className="text-left flex-1">
                          <div className="text-xs font-medium leading-4">{panel.title}</div>
                          <div className="mt-0.5 line-clamp-1 text-xs leading-4 text-gray-500">
                            {panel.description}
                          </div>
                        </div>
                      </button>
                      <button
                        id={`admin-panel-${panel.id}-favorite`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(panel.id);
                        }}
                        className={cn(
                          "absolute right-1.5 top-1.5 rounded p-1 transition-all duration-150 opacity-0 group-hover/panel:opacity-100",
                          isFavorite 
                            ? "text-yellow-500 hover:text-yellow-600 opacity-100" 
                            : "text-gray-400 hover:text-yellow-500"
                        )}
                        title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                      >
                        {isFavorite ? (
                          <Star className="w-4 h-4 fill-current" />
                        ) : (
                          <StarOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="app-page-shell flex-1 overflow-y-auto">
        {(() => {
          const CurrentComponent = componentMap[location.pathname];
          if (CurrentComponent) {
            return <CurrentComponent />;
          }
          
          return (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <Settings className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Select an admin panel from the sidebar</p>
                <p className="text-sm mt-2">Use search to quickly find specific settings</p>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default AdminCenter;
